import { Card, message, Space, Tag, Typography } from "antd";
import {
  fetchTrainingDetail,
  createTrainingGroup,
  fetchTrainingGroupData,
  fetchTeamsByMentor,
} from "PlatForms/OrgLMS/Services/AdminServices/trainingService";
import React, { useState, useEffect } from "react";
const { Text } = Typography;

const useTrainingGroup = () => {
  const adminId = localStorage.getItem("personalId");
  const token = localStorage.getItem("token");
  const [viewAll, setViewAll] = useState(false);
  const [mentors, setMentors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMentor, setSelectedMentor] = useState({ id: null, name: null });
  const [groupMembers, setGroupMembers] = useState([]);
  const [availableLearner, setAvailableLearner] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupData, setGroupData] = useState([]);
  const [groupsPagination, setGroupsPagination] = useState({
    totalCount: 0,
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
  });

  useEffect(() => {
    const fetchData = async () => {
      const { learners, mentors } = await fetchTrainingGroupData(adminId, token);
      setAvailableLearner(learners);
      setMentors(mentors);
      setLoading(false);
    };

    fetchGroupsData();
    fetchData();
  }, [adminId, token]);

  const fetchGroupsData = async (page = 1) => {
    setLoadingGroups(true);
    try {
      const data = await fetchTrainingDetail(adminId, token, {
        pageNumber: page,
        pageSize: groupsPagination.pageSize,
      });
      setGroupData(data.items || []);
      setGroupsPagination({
        totalCount: data.totalCount,
        pageNumber: data.pageNumber,
        pageSize: data.pageSize,
        totalPages: data.totalPages,
      });
    } catch (error) {
      message.error(error.message);
    }
    setLoadingGroups(false);
  };

  const handleGroupsPageChange = (page) => {
    fetchGroupsData(page);
  };

  const handleMentorSelect = async (mentor) => {
    setSelectedMentor({ id: mentor.mentorId, name: mentor.mentorName });
    setTeamsLoading(true);
    try {
      const mentorTeams = await fetchTeamsByMentor(mentor.mentorId, token);
      setTeams(mentorTeams);
    } catch (error) {
      message.error("Failed to load teams for this mentor");
      setTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  };

  const removeMember = (learner) => {
    setGroupMembers(groupMembers.filter((member) => member.learnerId !== learner.learnerId));
    setAvailableLearner([...availableLearner, learner]);
  };

  const handleDragStart = (event, learner) => {
    event.dataTransfer.setData("learnerId", learner.learnerId);
    event.dataTransfer.setData("learnerFirstName", learner.learnerFirstName);
    event.dataTransfer.setData("learnerLastName", learner.learnerLastName);
    event.dataTransfer.setData("title", learner.title);
    event.dataTransfer.setData("courseId", learner.courseId);
    event.dataTransfer.setData("trainingRequestId", learner.id); // Add this line
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const learnerId = Number(event.dataTransfer.getData("learnerId"));
    const learnerFirstName = event.dataTransfer.getData("learnerFirstName");
    const learnerLastName = event.dataTransfer.getData("learnerLastName");
    const title = event.dataTransfer.getData("title");
    const courseId = Number(event.dataTransfer.getData("courseId"));
    const trainingRequestId = Number(event.dataTransfer.getData("trainingRequestId")); // Add this line

    if (groupMembers.length >= 50) {
      message.error("You can only assign a maximum of 50 learners to a group!");
      return;
    }

    if (groupMembers.length > 0 && groupMembers[0].courseId !== courseId) {
      message.warning("Cannot add learners from different courses to the same group!");
      return;
    }

    setGroupMembers([
      ...groupMembers,
      {
        learnerFirstName,
        learnerLastName,
        learnerId,
        title,
        courseId,
        id: trainingRequestId, // Store the training request ID
      },
    ]);
    setAvailableLearner(availableLearner.filter((s) => s.learnerId !== learnerId));
  };

  const menuItems = mentors.map((mentor) => ({
    key: mentor.mentorName,
    style: { padding: 0 },
    onClick: () => handleMentorSelect(mentor),
    label: (
      <Card
        size="small"
        hoverable
        style={{ width: "100%", margin: "4px 0" }}
        styles={{ body: { padding: "12px" } }}
      >
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong>{mentor.mentorName}</Text>
            <Tag color="gold">⭐ {mentor.rating}</Tag>
          </div>

          <ul style={{ paddingLeft: 20, margin: 0 }}>
            {mentor.mentorSkills.map((skill, index) => (
              <li key={index} style={{ fontSize: 12, color: "rgba(0, 0, 0, 0.45)" }}>
                {skill}
              </li>
            ))}
          </ul>

          <Space size={8} style={{ marginTop: 4 }}>
            <Tag icon="👥">{mentor.noOfLearners} Learner </Tag>
            <Tag icon="📌">{mentor.noOfGroups} Groups</Tag>
            <Tag icon="">{mentor.noOfProjects} Projects Completed</Tag>
          </Space>
        </Space>
      </Card>
    ),
  }));

  const isButtonDisabled = groupMembers.length < 1 || !selectedGroup || !selectedMentor;

  const finalizeGroup = async () => {
    if (isButtonDisabled) return;

    const requestData = {
      adminId: parseInt(adminId),
      teamNameId: selectedGroup,
      mentorId: selectedMentor.id,
      trainingRequestIds: groupMembers.map((member) => member.id), // Now using the correct ID
    };

    setSubmitting(true);
    try {
      await createTrainingGroup(requestData, token);
      message.success("Group created successfully!");
      // Reset form after successful creation
      setGroupMembers([]);
      setSelectedGroup(null);
      setSelectedMentor({ id: null, name: null });
      // Refresh available learners
      const { learners } = await fetchTrainingGroupData(adminId, token);
      setAvailableLearner(learners);
    } catch (error) {
      console.log("Error", error);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    loadingGroups,
    groupData,
    groupsPagination,
    handleGroupsPageChange,
    loading,
    viewAll,
    setViewAll,
    menuItems,
    handleDragStart,
    handleDrop,
    groupMembers,
    isButtonDisabled,
    removeMember,
    availableLearner,
    selectedMentor,
    teams,
    teamsLoading,
    submitting,
    finalizeGroup,
    selectedGroup,
    setSelectedGroup,
  };
};

export default useTrainingGroup;
